import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSingletonHighlighter } from 'shiki';
import { cn } from '@/ui/lib/utils';
import { Button } from './button';
import { ScrollArea, ScrollBar } from './scroll-area';

type SupportedLang = 'ts' | 'js' | 'python' | 'html' | 'bash' | 'java';

const LANG_MAP: Record<SupportedLang, string> = {
	ts: 'typescript',
	js: 'javascript',
	python: 'python',
	html: 'html',
	bash: 'bash',
	java: 'java',
};

export interface CodeFile {
	filename: string;
	code: string;
	language?: SupportedLang;
}

interface MultiFileCodeBlockProps {
	files: CodeFile[];
	className?: string;
}

function HighlightedCode({
	code,
	language,
}: {
	code: string;
	language?: SupportedLang;
}) {
	const [html, setHtml] = useState<string | null>(null);

	useEffect(() => {
		const shikiLang = language ? (LANG_MAP[language] ?? 'text') : 'text';
		getSingletonHighlighter({
			themes: ['github-dark-default'],
			langs: [shikiLang],
		}).then((highlighter) => {
			setHtml(
				highlighter.codeToHtml(code, {
					lang: shikiLang,
					theme: 'github-dark-default',
				}),
			);
		});
	}, [code, language]);

	if (html) {
		return (
			<div
				className='text-xs [&>pre]:bg-transparent! [&>pre]:px-4 [&>pre]:py-3 [&>pre]:m-0 [&>pre]:font-mono'
				style={{ background: '#0d1117' }}
				// biome-ignore lint/security/noDangerouslySetInnerHtml: shiki output is safe
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	return (
		<pre
			className='px-4 py-3 text-xs font-mono text-white/70 whitespace-pre overflow-x-auto'
			style={{ background: '#0d1117' }}
		>
			{code}
		</pre>
	);
}

export function MultiFileCodeBlock({
	files,
	className,
}: MultiFileCodeBlockProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [copied, setCopied] = useState(false);

	const active = files[activeIndex];

	const handleCopy = () => {
		navigator.clipboard.writeText(active.code);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div
			className={cn(
				'relative group rounded-md overflow-hidden',
				className,
			)}
		>
			{/* File tabs */}
			<div
				className='flex items-end overflow-x-auto'
				style={{ background: '#010409' }}
			>
				{files.map((file, i) => (
					<button
						key={file.filename}
						type='button'
						onClick={() => setActiveIndex(i)}
						className={cn(
							'px-4 py-2 text-xs font-mono whitespace-nowrap border-b-2 transition-colors',
							i === activeIndex
								? 'text-white border-blue-400 bg-[#0d1117]'
								: 'text-white/40 border-transparent hover:text-white/70',
						)}
					>
						{file.filename}
					</button>
				))}
			</div>

			{/* Copy button */}
			<Button
				type='button'
				variant='ghost'
				size='icon'
				onClick={handleCopy}
				className='absolute top-9 right-1.5 z-10 h-7 w-7 text-white/50 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity'
			>
				{copied ? (
					<Check className='size-3.5 text-green-400' />
				) : (
					<Copy className='size-3.5' />
				)}
			</Button>

			{/* Code */}
			<ScrollArea>
				<HighlightedCode
					code={active.code}
					language={active.language}
				/>
				<ScrollBar orientation='horizontal' />
			</ScrollArea>
		</div>
	);
}
